import React, { useEffect, useState } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Sparkles,
  Globe,
  Loader2,
  X,
  Check,
  Download,
  Lock,
  Building2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { useUserRole } from '../../hooks/useUserRole';
import {
  listAiSkills,
  createAiSkill,
  updateAiSkill,
  deleteAiSkill,
} from '../../services/aiSkillsService';
import type {
  AiSkill,
  AiSkillCategory,
  CreateAiSkillData,
} from '../../services/aiSkillsService';
import { getUserOrganizations } from '../../services/organizationService';
import type { Organization } from '../../types/organization';

// ─── Org Multi-Select ────────────────────────────────────────────────────────

type OrgMultiSelectProps = {
  orgs: Organization[];
  selected: string[];
  onChange: (ids: string[]) => void;
  isLoading: boolean;
};

const OrgMultiSelect: React.FC<OrgMultiSelectProps> = ({
  orgs,
  selected,
  onChange,
  isLoading,
}) => {
  const toggle = (id: string) => {
    onChange(
      selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]
    );
  };

  const allSelected =
    orgs.length > 0 && orgs.every(o => selected.includes(o.id));
  const toggleAll = () => onChange(allSelected ? [] : orgs.map(o => o.id));

  if (isLoading) {
    return (
      <div className='flex items-center gap-2 text-muted-foreground text-sm py-2'>
        <Loader2 className='h-3.5 w-3.5 animate-spin' />
        Loading organisations...
      </div>
    );
  }

  if (orgs.length === 0) {
    return (
      <p className='text-sm text-muted-foreground'>No organisations found.</p>
    );
  }

  return (
    <div className='rounded-md border border-input bg-background overflow-hidden'>
      {/* Select all row */}
      <label className='flex items-center gap-2.5 px-3 py-2 border-b border-input bg-muted/30 cursor-pointer hover:bg-muted/50 select-none'>
        <input
          type='checkbox'
          checked={allSelected}
          onChange={toggleAll}
          className='h-4 w-4 rounded border-input'
        />
        <span className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
          {allSelected ? 'Deselect all' : 'Select all'}
        </span>
      </label>
      <div className='max-h-40 overflow-y-auto'>
        {orgs.map(org => (
          <label
            key={org.id}
            className='flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-muted/40 select-none'
          >
            <input
              type='checkbox'
              checked={selected.includes(org.id)}
              onChange={() => toggle(org.id)}
              className='h-4 w-4 rounded border-input'
            />
            <span className='text-sm text-foreground'>{org.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// ─── Skill Form (inline create/edit) ─────────────────────────────────────────

type SkillFormProps = {
  initial?: AiSkill;
  onSave: (data: CreateAiSkillData) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
};

const SkillForm: React.FC<SkillFormProps> = ({
  initial,
  onSave,
  onCancel,
  isSaving,
}) => {
  const [skillName, setSkillName] = useState(initial?.skill_name ?? '');
  const [systemPrompt, setSystemPrompt] = useState(
    initial?.system_prompt_md ?? ''
  );
  const [category, setCategory] = useState<AiSkillCategory>(
    initial?.category ?? 'policy_generation'
  );
  // For imported global skills, default to shared (is_personal = false)
  const [isPersonal, setIsPersonal] = useState(
    initial
      ? initial.created_by !== null
        ? initial.is_personal
        : false
      : false
  );
  const [orgIds, setOrgIds] = useState<string[]>(
    initial?.organisation_ids ?? []
  );
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);

  useEffect(() => {
    setOrgsLoading(true);
    getUserOrganizations()
      .then(({ organizations }) => {
        setOrgs(organizations);
        // Auto-select all orgs when creating a new shared skill
        if (!initial && !isPersonal && orgIds.length === 0) {
          setOrgIds(organizations.map(o => o.id));
        }
      })
      .catch(() => setOrgs([]))
      .finally(() => setOrgsLoading(false));
  }, []);

  // When switching to shared, auto-select all orgs
  const handlePersonalToggle = (personal: boolean) => {
    setIsPersonal(personal);
    if (!personal && orgIds.length === 0) {
      setOrgIds(orgs.map(o => o.id));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave({
      skill_name: skillName,
      system_prompt_md: systemPrompt,
      category,
      is_personal: isPersonal,
      organisation_ids: isPersonal ? [] : orgIds,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className='space-y-4 p-5 rounded-xl border border-border bg-muted/30'
    >
      <div className='space-y-1.5'>
        <label className='text-sm font-medium text-foreground'>
          Skill Name
        </label>
        <input
          type='text'
          value={skillName}
          onChange={e => setSkillName(e.target.value)}
          placeholder='e.g. Construction Safety'
          required
          className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring'
        />
      </div>

      <div className='space-y-1.5'>
        <label className='text-sm font-medium text-foreground'>
          System Prompt (Markdown)
        </label>
        <p className='text-xs text-muted-foreground'>
          Instructions injected into the AI system prompt when this skill is
          selected.
        </p>
        <textarea
          value={systemPrompt}
          onChange={e => setSystemPrompt(e.target.value)}
          placeholder='## My Skill&#10;&#10;When generating this policy:&#10;- Use formal language...'
          required
          rows={8}
          className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring resize-y'
        />
      </div>

      {/* Category */}
      <div className='space-y-1.5'>
        <label className='text-sm font-medium text-foreground'>
          Category
        </label>
        <select
          value={category}
          onChange={e => setCategory(e.target.value as AiSkillCategory)}
          className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring'
        >
          <option value='policy_generation'>Policy Generation</option>
          <option value='system'>System</option>
        </select>
      </div>

      {/* Access */}
      <div className='space-y-2'>
        <label className='text-sm font-medium text-foreground'>
          Who can access this skill?
        </label>
        <div className='flex gap-2'>
          <button
            type='button'
            onClick={() => handlePersonalToggle(false)}
            className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-colors ${
              !isPersonal
                ? 'border-primary bg-primary/5 text-foreground'
                : 'border-input text-muted-foreground hover:bg-muted/40'
            }`}
          >
            <Building2 className='h-4 w-4 shrink-0' />
            <div className='text-left'>
              <p className='font-medium text-inherit'>Organisations</p>
              <p className='text-xs text-muted-foreground'>
                Shared with selected orgs
              </p>
            </div>
          </button>
          <button
            type='button'
            onClick={() => handlePersonalToggle(true)}
            className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-colors ${
              isPersonal
                ? 'border-primary bg-primary/5 text-foreground'
                : 'border-input text-muted-foreground hover:bg-muted/40'
            }`}
          >
            <Lock className='h-4 w-4 shrink-0' />
            <div className='text-left'>
              <p className='font-medium text-inherit'>Personal only</p>
              <p className='text-xs text-muted-foreground'>
                Only visible to you
              </p>
            </div>
          </button>
        </div>
      </div>

      {/* Org picker — only when shared */}
      {!isPersonal && (
        <div className='space-y-1.5'>
          <label className='text-sm font-medium text-foreground'>
            Organisations
            {orgIds.length > 0 && (
              <span className='ml-1.5 text-xs text-muted-foreground font-normal'>
                {orgIds.length} selected
              </span>
            )}
          </label>
          <OrgMultiSelect
            orgs={orgs}
            selected={orgIds}
            onChange={setOrgIds}
            isLoading={orgsLoading}
          />
          {orgIds.length === 0 && !orgsLoading && (
            <p className='text-xs text-destructive'>
              Select at least one organisation.
            </p>
          )}
        </div>
      )}

      <div className='flex items-center gap-2 pt-1'>
        <Button
          type='submit'
          disabled={isSaving || (!isPersonal && orgIds.length === 0)}
          size='sm'
        >
          {isSaving ? (
            <>
              <Loader2 className='h-3.5 w-3.5 mr-1.5 animate-spin' />
              Saving...
            </>
          ) : (
            <>
              <Check className='h-3.5 w-3.5 mr-1.5' />
              {initial ? 'Save changes' : 'Create skill'}
            </>
          )}
        </Button>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          onClick={onCancel}
          disabled={isSaving}
        >
          <X className='h-3.5 w-3.5 mr-1.5' />
          Cancel
        </Button>
      </div>
    </form>
  );
};

// ─── Skill Row ────────────────────────────────────────────────────────────────

type SkillRowProps = {
  skill: AiSkill;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
  onImport?: () => void;
  adminCanEdit?: boolean;
};

const SkillRow: React.FC<SkillRowProps> = ({
  skill,
  onEdit,
  onDelete,
  isDeleting,
  onImport,
  adminCanEdit,
}) => {
  const isGlobal = skill.created_by === null;
  const [expanded, setExpanded] = useState(false);

  return (
    <div className='rounded-xl border border-border bg-card p-4 space-y-2'>
      <div className='flex items-start justify-between gap-3'>
        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-2 flex-wrap'>
            <p className='text-sm font-semibold text-foreground'>
              {skill.skill_name}
            </p>
            <Badge
              variant='outline'
              className={`text-xs gap-1 ${
                skill.category === 'system'
                  ? 'border-blue-200 text-blue-700 bg-blue-50'
                  : 'border-purple-200 text-purple-700 bg-purple-50'
              }`}
            >
              {skill.category === 'system' ? (
                <><Building2 className='h-3 w-3' />System</>
              ) : (
                <><Sparkles className='h-3 w-3' />Policy Generation</>
              )}
            </Badge>
            {isGlobal ? (
              <Badge
                variant='outline'
                className='text-xs text-muted-foreground gap-1'
              >
                <Globe className='h-3 w-3' />
                Global
              </Badge>
            ) : skill.is_personal ? (
              <Badge
                variant='outline'
                className='text-xs text-muted-foreground gap-1'
              >
                <Lock className='h-3 w-3' />
                Personal
              </Badge>
            ) : (
              <Badge
                variant='outline'
                className='text-xs text-muted-foreground gap-1'
              >
                <Building2 className='h-3 w-3' />
                {skill.organisation_ids.length > 0
                  ? `${skill.organisation_ids.length} org${skill.organisation_ids.length !== 1 ? 's' : ''}`
                  : 'Shared'}
              </Badge>
            )}
          </div>
          <button
            onClick={() => setExpanded(v => !v)}
            className='mt-1 text-xs text-muted-foreground hover:text-foreground transition-colors'
          >
            {expanded ? 'Hide prompt ↑' : 'View prompt ↓'}
          </button>
        </div>

        <div className='flex items-center gap-1 shrink-0'>
          {isGlobal && !adminCanEdit && onImport && (
            <Button
              variant='outline'
              size='sm'
              onClick={onImport}
              className='h-7 px-2 text-xs gap-1'
            >
              <Download className='h-3 w-3' />
              Import
            </Button>
          )}
          {(!isGlobal || adminCanEdit) && (
            <>
              <Button
                variant='ghost'
                size='sm'
                onClick={onEdit}
                className='h-7 w-7 p-0'
              >
                <Pencil className='h-3.5 w-3.5' />
              </Button>
              <Button
                variant='ghost'
                size='sm'
                onClick={onDelete}
                disabled={isDeleting}
                className='h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10'
              >
                {isDeleting ? (
                  <Loader2 className='h-3.5 w-3.5 animate-spin' />
                ) : (
                  <Trash2 className='h-3.5 w-3.5' />
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      {expanded && (
        <pre className='mt-2 p-3 rounded-lg bg-muted text-xs font-mono whitespace-pre-wrap break-words text-muted-foreground border border-border'>
          {skill.system_prompt_md}
        </pre>
      )}
    </div>
  );
};

// ─── Main Section ─────────────────────────────────────────────────────────────

export const AiSkillsSection: React.FC = () => {
  const { toast } = useToast();
  const { isAdmin } = useUserRole();
  const [skills, setSkills] = useState<AiSkill[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [importingFrom, setImportingFrom] = useState<AiSkill | null>(null);

  const load = async () => {
    setIsLoading(true);
    try {
      const data = await listAiSkills();
      setSkills(data);
    } catch (err) {
      toast({
        title: 'Failed to load AI skills',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (data: CreateAiSkillData) => {
    setIsSaving(true);
    try {
      await createAiSkill(data);
      toast({
        title: importingFrom
          ? 'Skill imported to your organisation'
          : 'Skill created',
      });
      setShowCreateForm(false);
      setImportingFrom(null);
      await load();
    } catch (err) {
      toast({
        title: 'Failed to create skill',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async (id: string, data: CreateAiSkillData) => {
    setIsSaving(true);
    try {
      await updateAiSkill(id, data);
      toast({ title: 'Skill updated' });
      setEditingId(null);
      await load();
    } catch (err) {
      toast({
        title: 'Failed to update skill',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteAiSkill(id);
      toast({ title: 'Skill deleted' });
      setSkills(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      toast({
        title: 'Failed to delete skill',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const globalSkills = skills.filter(s => s.created_by === null);
  const mySkills = skills.filter(s => s.created_by !== null);

  return (
    <div className='space-y-6'>
      {/* Header card */}
      <div className='dashboard-card p-6'>
        <div className='flex items-center justify-between mb-2'>
          <div className='flex items-center gap-3'>
            <div className='p-2 rounded-lg bg-primary/10'>
              <Sparkles className='h-5 w-5 text-primary' />
            </div>
            <div>
              <h3 className='text-lg font-semibold text-foreground'>
                AI Skills
              </h3>
              <p className='text-sm text-muted-foreground'>
                Reusable system-prompt instructions injected during policy
                generation in Boost Policy.
              </p>
            </div>
          </div>
          <Button
            size='sm'
            onClick={() => {
              setShowCreateForm(true);
              setEditingId(null);
              setImportingFrom(null);
            }}
            disabled={showCreateForm && !importingFrom}
          >
            <Plus className='h-4 w-4 mr-1.5' />
            New Skill
          </Button>
        </div>
      </div>

      {/* Create form */}
      {showCreateForm && !importingFrom && (
        <div className='dashboard-card p-6'>
          <h4 className='text-sm font-semibold text-foreground mb-4'>
            New Skill
          </h4>
          <SkillForm
            onSave={handleCreate}
            onCancel={() => setShowCreateForm(false)}
            isSaving={isSaving}
          />
        </div>
      )}

      {/* Import form (pre-populated from a global skill) */}
      {importingFrom && (
        <div className='dashboard-card p-6'>
          <div className='mb-4'>
            <h4 className='text-sm font-semibold text-foreground'>
              Import Global Skill
            </h4>
            <p className='text-xs text-muted-foreground mt-0.5'>
              Customise and save a copy of{' '}
              <strong>{importingFrom.skill_name}</strong> for your organisation.
            </p>
          </div>
          <SkillForm
            initial={importingFrom}
            onSave={handleCreate}
            onCancel={() => setImportingFrom(null)}
            isSaving={isSaving}
          />
        </div>
      )}

      {/* My skills */}
      <div className='dashboard-card p-6 space-y-4'>
        <div className='flex items-center justify-between'>
          <h4 className='text-sm font-semibold text-foreground'>My Skills</h4>
          {!isLoading && mySkills.length > 0 && (
            <span className='text-xs text-muted-foreground'>
              {mySkills.length} skill{mySkills.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {isLoading ? (
          <div className='flex items-center gap-2 py-4 text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' />
            <span className='text-sm'>Loading skills...</span>
          </div>
        ) : mySkills.length === 0 ? (
          <div className='py-8 text-center text-sm text-muted-foreground'>
            <Sparkles className='h-8 w-8 mx-auto mb-2 opacity-30' />
            No skills yet. Create one to tailor AI generation for your
            organisation.
          </div>
        ) : (
          <div className='space-y-3'>
            {mySkills.map(skill =>
              editingId === skill.id ? (
                <SkillForm
                  key={skill.id}
                  initial={skill}
                  onSave={data => handleUpdate(skill.id, data)}
                  onCancel={() => setEditingId(null)}
                  isSaving={isSaving}
                />
              ) : (
                <SkillRow
                  key={skill.id}
                  skill={skill}
                  onEdit={() => {
                    setEditingId(skill.id);
                    setShowCreateForm(false);
                  }}
                  onDelete={() => handleDelete(skill.id)}
                  isDeleting={deletingId === skill.id}
                />
              )
            )}
          </div>
        )}
      </div>

      {/* Global skills (read-only) */}
      {globalSkills.length > 0 && (
        <div className='dashboard-card p-6 space-y-4'>
          <div>
            <h4 className='text-sm font-semibold text-foreground'>
              Global Skills
            </h4>
            <p className='text-xs text-muted-foreground mt-0.5'>
              Provided by Revisr — available to all organisations.
              {isAdmin ? ' Editable as admin.' : ' Import a copy to customise.'}
            </p>
          </div>
          <div className='space-y-3'>
            {globalSkills.map(skill =>
              editingId === skill.id ? (
                <SkillForm
                  key={skill.id}
                  initial={skill}
                  onSave={data => handleUpdate(skill.id, data)}
                  onCancel={() => setEditingId(null)}
                  isSaving={isSaving}
                />
              ) : (
                <SkillRow
                  key={skill.id}
                  skill={skill}
                  onEdit={() => {
                    setEditingId(skill.id);
                    setShowCreateForm(false);
                    setImportingFrom(null);
                  }}
                  onDelete={() => handleDelete(skill.id)}
                  isDeleting={deletingId === skill.id}
                  adminCanEdit={isAdmin}
                  onImport={() => {
                    setImportingFrom(skill);
                    setShowCreateForm(false);
                    setEditingId(null);
                  }}
                />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};
